import { BaseApiService, unwrapApiResponse } from './base';
import { snakeToCamel } from '../../utils/caseTransform';
import {
  FileEvent,
  FileJourney,
  FileEventSearchParams,
  FileEventSearchResult
} from '../../types/fileAudit';

// Derive a status string from the event.
function deriveStatus(event: any): string {
  if (event.status) return event.status;
  if (typeof event.success === 'boolean') {
    return event.success ? 'success' : 'failed';
  }
  return event.eventType || '';
}

// Transform API response event to camelCase FileEvent type using generic converter
function transformFileEvent(apiEvent: any): FileEvent {
  // Preserve metadata before generic conversion (may have arbitrary keys)
  const baseMeta = apiEvent.metadata || apiEvent.details || {};
  // THUB-205: capture the notification block VERBATIM before snakeToCamel runs.
  // Its messageAttributes are snake_case (AWS filter convention) and body.metadata
  // holds user-defined keys — normalizing either would misrepresent the exact
  // published message the panel exists to show. null when absent/failed publish.
  const rawNotification = apiEvent.notification ?? null;
  const ev = snakeToCamel<any>(apiEvent);

  // Merge workflowStep into metadata so the journey timeline can display it
  const metadata = ev.workflowStep ? { ...baseMeta, workflowStep: ev.workflowStep } : baseMeta;

  return {
    eventId: ev.eventId || '',
    fileId: ev.fileId || '',
    partnerId: ev.partnerId || '',
    partnerName: ev.partnerName || '',
    taskId: ev.taskId || '',
    taskName: ev.taskName || '',
    filename: ev.filename || '',
    eventType: ev.eventType || 'PROCESSING',
    status: deriveStatus(ev),
    timestamp: ev.timestamp || ev.eventTimestamp || '',
    sourcePath: ev.sourcePath || ev.sourceLocation,
    destinationPath: ev.destinationPath || ev.destinationLocation,
    // THUB-197: carry the bare S3 object key so the row/journey can build a
    // download request. The backend stores it on each event as s3Key.
    s3Key: ev.s3Key,
    fileSize: ev.fileSize,
    sizeBytes: ev.sizeBytes ?? ev.fileSize,
    checksum: ev.checksum,
    errorMessage: ev.errorMessage,
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
    // THUB-205: verbatim notification block (never normalized). null when the
    // event never published or the publish failed -> panel renders nothing.
    notification: rawNotification
  };
}

/**
 * Build flat query string for the file audit API.
 * Backend expects: ?filename=X&partnerId=Y&limit=N&cursor=X
 */
function buildFileAuditQueryString(params: FileEventSearchParams): string {
  const parts: string[] = [];

  if (params.filename) {
    parts.push(`filename=${encodeURIComponent(params.filename)}`);
  }
  if (params.partnerId) {
    parts.push(`partnerId=${encodeURIComponent(params.partnerId)}`);
  }
  if (params.taskId) {
    parts.push(`taskId=${encodeURIComponent(params.taskId)}`);
  }
  if (params.eventType) {
    parts.push(`eventType=${encodeURIComponent(params.eventType)}`);
  }
  if (params.startDate) {
    parts.push(`startDate=${encodeURIComponent(params.startDate)}`);
  }
  if (params.endDate) {
    parts.push(`endDate=${encodeURIComponent(params.endDate)}`);
  }
  if (params.limit) {
    parts.push(`limit=${params.limit}`);
  }
  if (params.cursor) {
    parts.push(`cursor=${encodeURIComponent(params.cursor)}`);
  }
  // THUB-244: opt-in one-row-per-file grouping.
  if (params.groupBy) {
    parts.push(`groupBy=${encodeURIComponent(params.groupBy)}`);
  }

  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export class FileAuditApiService extends BaseApiService {
  /**
   * Search for file events with optional filters.
   * Sends flat query params matching the backend's expected format.
   */
  async searchFileEvents(params: FileEventSearchParams): Promise<FileEventSearchResult> {
    const queryString = buildFileAuditQueryString(params);
    const response = await this.get(`/file-events${queryString}`);
    const jsonData = await response.json();

    // Unwrap the {success, data} envelope
    const payload = unwrapApiResponse(jsonData);

    const p = snakeToCamel<any>(payload);

    return {
      events: (p.events || []).map(transformFileEvent),
      nextCursor: p.nextCursor || null,
      totalCount: p.totalCount
    };
  }

  /**
   * Get the complete journey of a file through the system.
   * Backend returns: { success, data: { fileId, journey: [...], summary: {...} } }
   */
  async getFileJourney(fileId: string): Promise<FileJourney> {
    const response = await this.get(`/file-events/${fileId}/journey`);
    const jsonData = await response.json();

    // Unwrap the {success, data} envelope
    const payload = unwrapApiResponse(jsonData);

    const p = snakeToCamel<any>(payload);
    const journeyEvents = p.journey || p.events || [];
    const summary = p.summary || {};

    const events = journeyEvents.map(transformFileEvent);

    // THUB-197: derive the archive location for the download action. Prefer the
    // s3Key of the archive/ready event (the final resting place), falling back
    // to the last event that carries an archive/ key, then any event with a key.
    const archiveEvent =
      events.find((e: FileEvent) => e.s3Key && e.s3Key.startsWith('archive/')) ||
      [...events].reverse().find((e: FileEvent) => !!e.s3Key);
    const archivePath = archiveEvent?.s3Key;
    const partnerId =
      summary.partnerId || events.find((e: FileEvent) => e.partnerId)?.partnerId || '';

    return {
      fileId: p.fileId || fileId,
      filename: summary.filename || (journeyEvents.length > 0 ? journeyEvents[0].filename : ''),
      partnerId,
      partnerName: summary.partnerName,
      taskId: summary.taskId || '',
      taskName: summary.taskName,
      archivePath,
      events
    };
  }

  /**
   * Get file events for a specific partner
   */
  async getPartnerFileEvents(
    partnerId: string,
    params?: Omit<FileEventSearchParams, 'partnerId'>
  ): Promise<FileEventSearchResult> {
    return this.searchFileEvents({
      ...params,
      partnerId
    });
  }

  /**
   * Get file events for a specific task
   */
  async getTaskFileEvents(
    taskId: string,
    params?: Omit<FileEventSearchParams, 'taskId'>
  ): Promise<FileEventSearchResult> {
    return this.searchFileEvents({
      ...params,
      taskId
    });
  }

  /**
   * Get recent file events (last N events)
   */
  async getRecentFileEvents(limit: number = 50): Promise<FileEventSearchResult> {
    return this.searchFileEvents({ limit });
  }

  /**
   * Get file events by date range
   */
  async getFileEventsByDateRange(
    startDate: string,
    endDate: string,
    params?: Omit<FileEventSearchParams, 'startDate' | 'endDate'>
  ): Promise<FileEventSearchResult> {
    return this.searchFileEvents({
      ...params,
      startDate,
      endDate
    });
  }
}

// Export singleton instance
export const fileAuditApi = new FileAuditApiService();
